import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Wallet, Search, Building2, Calendar, DollarSign, Loader2,
  ExternalLink, RefreshCw, Filter, AlertCircle, Send, CheckSquare,
  Square, Plus,
} from 'lucide-react';


export default function PaymentQueue() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [dueBeforeFilter, setDueBeforeFilter] = useState('');
  // Wave F3-C — multi-select for creating a Payment Run.
  const [selected, setSelected] = useState(new Set());
  const [creatingRun, setCreatingRun] = useState(false);
  const [plannedDate, setPlannedDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (dueBeforeFilter) params.due_before = dueBeforeFilter;
      const { data } = await axiosInstance.get(
        '/bill-review/ready-for-payment', { params });
      setBills(data.bills || []);
      setTotal(data.total || 0);
      setTotalPayable(data.total_payable || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل التَّحميل');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = bills.filter((b) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (b.supplier_name || '').toLowerCase().includes(s)
      || (b.bill_number || '').toLowerCase().includes(s)
      || (b.supplier_bill_no || '').toLowerCase().includes(s)
      || (b.journal_no || '').toLowerCase().includes(s);
  });

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (bill) => bill.due_date && bill.due_date < today;

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const selectedTotal = useMemo(() => {
    return bills
      .filter((b) => selected.has(b.id))
      .reduce((a, b) => a + Number(b.payable_amount || b.total || 0), 0);
  }, [bills, selected]);

  const createRun = async () => {
    if (selected.size === 0) return;
    setCreatingRun(true);
    try {
      const { data } = await axiosInstance.post('/payment-runs', {
        bill_ids:             Array.from(selected),
        planned_payment_date: plannedDate || null,
      });
      if (data.error) { toast.error(data.error); return; }
      toast.success(`تَم إنشاء دُفعَة الدَّفع ${data.run.run_no}`);
      setSelected(new Set());
      await load();
      navigate(`/payment-runs/${data.run.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل إنشاء الدُّفعَة');
    } finally { setCreatingRun(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4" dir="rtl" data-testid="payment-queue">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" />
              قائِمَة انتِظار المَدفوعات
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              الفَواتير المُعتَمَدَة وَالمُرَحَّلَة الجاهِزَة لِلدَّفع
            </p>
          </div>
          <Link to="/payment-runs"
                 data-testid="link-runs"
                 className="text-xs font-semibold rounded-lg border border-indigo-300
                            text-indigo-700 hover:bg-indigo-50 px-3 py-2
                            inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> دُفَعات الدَّفع
          </Link>
        </div>

        {/* Selection action bar */}
        {selected.size > 0 && (
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 mb-3
                          flex flex-wrap items-center justify-between gap-2"
               data-testid="pq-action-bar">
            <div className="text-[12px] font-semibold text-emerald-900">
              <CheckSquare className="w-4 h-4 inline mr-1" />
              مُختار: {selected.size} فاتورَة · إجمالي:{' '}
              <span className="font-mono">
                {Number(selectedTotal).toLocaleString('en')} ر.س
              </span>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={plannedDate}
                      onChange={(e) => setPlannedDate(e.target.value)}
                      data-testid="pq-planned-date"
                      className="rounded border border-emerald-300 px-2 py-1 text-xs"
                      title="تاريخ الدَّفع المُخَطَّط" />
              <button onClick={createRun} disabled={creatingRun}
                       data-testid="pq-create-run-btn"
                       className="text-xs font-bold rounded-lg bg-emerald-600
                                  hover:bg-emerald-700 text-white px-3 py-2
                                  inline-flex items-center gap-1 disabled:opacity-50">
                {creatingRun
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Plus className="w-3.5 h-3.5" />}
                إنشاء دُفعَة دَفع
              </button>
              <button onClick={() => setSelected(new Set())}
                       data-testid="pq-clear-selection"
                       className="text-xs rounded border border-slate-300 px-2 py-2">
                إلغاء
              </button>
            </div>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="rounded-xl border bg-white p-3" data-testid="kpi-total-bills">
            <div className="text-[10px] font-semibold text-slate-500 uppercase mb-1">
              عَدَد الفَواتير
            </div>
            <div className="text-2xl font-bold text-slate-800">{total}</div>
          </div>
          <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3"
               data-testid="kpi-total-payable">
            <div className="text-[10px] font-semibold text-emerald-700 uppercase mb-1">
              إجمالي المُستَحَق
            </div>
            <div className="text-2xl font-bold text-emerald-800 font-mono">
              {Number(totalPayable).toLocaleString('en')} <span className="text-sm">ر.س</span>
            </div>
          </div>
          <div className="rounded-xl border bg-rose-50 border-rose-200 p-3"
               data-testid="kpi-overdue">
            <div className="text-[10px] font-semibold text-rose-700 uppercase mb-1">
              مُتَأَخِّرَة
            </div>
            <div className="text-2xl font-bold text-rose-800">
              {bills.filter(isOverdue).length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border bg-white p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="بَحث بِالمورد / رَقم الفاتورَة / رَقم القَيد…"
                    data-testid="pq-search"
                    className="w-full rounded border border-slate-300 pr-7 pl-2 py-1.5 text-xs" />
          </div>
          <input type="date" value={dueBeforeFilter}
                  onChange={(e) => setDueBeforeFilter(e.target.value)}
                  data-testid="pq-due-before"
                  className="rounded border border-slate-300 px-2 py-1.5 text-xs" />
          <button
            onClick={load} disabled={loading}
            data-testid="pq-refresh"
            className="text-xs font-semibold rounded bg-slate-100 hover:bg-slate-200
                       px-2 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            تَحديث
          </button>
        </div>

        {/* Table */}
        <div className="rounded-xl border bg-white overflow-hidden">
          {filtered.length === 0 && !loading ? (
            <div className="p-8 text-center text-slate-400 text-xs">
              <Wallet className="w-10 h-10 mx-auto opacity-40 mb-2" />
              لا فَواتير جاهِزَة لِلدَّفع حالِيّاً
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 border-b text-slate-600">
                <tr>
                  <th className="py-2 px-2 w-8"></th>
                  <th className="text-right py-2 px-3">FL ID</th>
                  <th className="text-right py-2 px-3">الفاتورَة</th>
                  <th className="text-right py-2 px-3">المُورِد</th>
                  <th className="text-right py-2 px-3">قَيد</th>
                  <th className="text-right py-2 px-3">تاريخ</th>
                  <th className="text-right py-2 px-3">استِحقاق</th>
                  <th className="text-left py-2 px-3">المُستَحَق</th>
                  <th className="py-2 px-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const overdue = isOverdue(b);
                  const isSelected = selected.has(b.id);
                  return (
                    <tr key={b.id}
                        onClick={() => toggle(b.id)}
                        className={`border-b last:border-b-0 hover:bg-slate-50 cursor-pointer
                                    ${overdue ? 'bg-rose-50/40' : ''}
                                    ${isSelected ? 'bg-emerald-50/60' : ''}`}
                        data-testid={`pq-row-${b.id}`}>
                      <td className="text-center py-2 px-2">
                        {isSelected
                          ? <CheckSquare className="w-4 h-4 text-emerald-600 mx-auto"
                                          data-testid={`pq-check-on-${b.id}`} />
                          : <Square className="w-4 h-4 text-slate-300 mx-auto"
                                     data-testid={`pq-check-off-${b.id}`} />}
                      </td>
                      <td className="py-2 px-3 font-mono text-[10px] text-slate-500">
                        {b.financial_lifecycle_id || '—'}
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-mono font-semibold">{b.bill_number}</div>
                        {b.supplier_bill_no && (
                          <div className="text-[9px] text-slate-500 font-mono">
                            {b.supplier_bill_no}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <div className="inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-slate-400" />
                          {b.supplier_name || '—'}
                        </div>
                      </td>
                      <td className="py-2 px-3 font-mono text-[10px] text-indigo-700">
                        {b.journal_no || '—'}
                      </td>
                      <td className="py-2 px-3 font-mono text-[10px]">
                        {b.bill_date}
                      </td>
                      <td className={`py-2 px-3 font-mono text-[10px]
                                       ${overdue ? 'text-rose-700 font-semibold' : ''}`}>
                        {b.due_date}
                        {overdue && (
                          <AlertCircle className="w-3 h-3 inline mr-1 text-rose-600" />
                        )}
                      </td>
                      <td className="text-left py-2 px-3 font-mono font-semibold text-emerald-700">
                        {Number(b.payable_amount || b.total || 0).toLocaleString('en')}
                      </td>
                      <td className="text-center py-2 px-3">
                        <Link to={`/bill-review/${b.id}`}
                               target="_blank"
                               onClick={(e) => e.stopPropagation()}
                               className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                               data-testid={`pq-open-${b.id}`}>
                          <ExternalLink className="w-3 h-3" /> فَتح
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
